import type { Action } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo } from 'react';

import {
  CREATE_AGENT_RUN_FROM_API_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_N8N_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_POWER_AUTOMATE_FUNCTION_ID,
  CREATE_AGENT_RUN_FROM_ZAPIER_FUNCTION_ID,
  CREATE_PREDICTION_FUNCTION_ID,
  CREATE_VALIDATION_TASK_FUNCTION_ID,
} from '@/constants';
import {
  useBatchGetActions,
  useBatchGetHooks,
  useBatchGetModels,
  useBatchGetValidations,
  useGetAgent,
} from '@/hooks/api';

const findIds = (str: string, resourceName: string) => {
  const regExp = new RegExp(`cradl:${resourceName}:[a-zA-Z0-9_-]+`, 'g');
  return [...str.matchAll(regExp)].map((reg) => reg.toString());
};

export type UseAgentTreeOpts = {
  agentId: string;
};

export const useAgentTree = ({ agentId }: UseAgentTreeOpts) => {
  const { data: agent, isPending: isPendingAgent } = useGetAgent({ agentId });

  const { data: hooks, isPending: isPendingHooks } = useBatchGetHooks(
    agent?.resourceIds.filter((r) => r.startsWith('cradl:hook')).map((hookId) => ({ hookId }))
  );
  const { data: actions, isPending: isPendingActions } = useBatchGetActions(
    agent?.resourceIds.filter((r) => r.startsWith('cradl:action')).map((actionId) => ({ actionId }))
  );
  const { data: models, isPending: isPendingModels } = useBatchGetModels(
    agent?.resourceIds.filter((r) => r.startsWith('cradl:model')).map((modelId) => ({ modelId }))
  );
  const { data: validations, isPending: isPendingValidations } = useBatchGetValidations(
    agent?.resourceIds.filter((r) => r.startsWith('cradl:validation')).map((validationId) => ({ validationId }))
  );

  const { knownDependencies, nodeResourceIds } = useMemo(() => {
    const knownDependencies: Record<string, string[]> = {
      'Document is Created': [
        CREATE_AGENT_RUN_FROM_API_FUNCTION_ID,
        CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID,
        CREATE_AGENT_RUN_FROM_N8N_FUNCTION_ID,
        CREATE_AGENT_RUN_FROM_POWER_AUTOMATE_FUNCTION_ID,
        CREATE_AGENT_RUN_FROM_ZAPIER_FUNCTION_ID,
      ],
      'Prediction is Created': [CREATE_PREDICTION_FUNCTION_ID],
      'ValidationTask has Completed': [CREATE_VALIDATION_TASK_FUNCTION_ID],
      'ValidationTask is Created': [CREATE_VALIDATION_TASK_FUNCTION_ID],
    };
    const nodeResourceIds: Record<string, Set<string>> = {};
    for (const action of actions) {
      nodeResourceIds[action.actionId] = new Set([action.actionId]);
      if (action.functionId === CREATE_PREDICTION_FUNCTION_ID) {
        const configStr = JSON.stringify(action.config);
        for (const modelId of findIds(configStr, 'model')) {
          if (models.find((m) => m.modelId === modelId)) {
            nodeResourceIds[action.actionId].add(modelId);
          }
        }
      }
      if (action.functionId === CREATE_VALIDATION_TASK_FUNCTION_ID) {
        const configStr = JSON.stringify(action.config);
        for (const validationId of findIds(configStr, 'validation')) {
          if (validations.find((m) => m.validationId === validationId)) {
            nodeResourceIds[action.actionId].add(validationId);
          }
        }
      }
    }

    return { knownDependencies, nodeResourceIds };
  }, [actions, models, validations]);

  const isConnected = useCallback(
    (from_action: Action, to_action: Action) => {
      for (const hook of hooks) {
        const deps = knownDependencies[hook.trigger] ?? [];
        if (
          (hook.trueActionId === to_action.actionId || hook.falseActionId === to_action.actionId) &&
          deps.includes(from_action.functionId)
        ) {
          nodeResourceIds[to_action.actionId].add(hook.hookId);
          return true;
        }
      }
      return false;
    },
    [hooks, knownDependencies, nodeResourceIds]
  );

  const length = actions.length;
  const connections = useMemo(() => {
    const connections = Array.from({ length }, () => Array.from({ length }, () => false));
    for (const [i, from_action] of actions.entries()) {
      for (const [j, to_action] of actions.entries()) {
        if (isConnected(from_action, to_action)) {
          connections[i][j] = true;
        }
      }
    }

    return connections;
  }, [actions, isConnected, length]);

  const resources = useMemo(() => {
    const resources: string[][] = [];
    for (const action of actions) {
      resources.push([...nodeResourceIds[action.actionId]]);
    }

    return resources;
  }, [actions, nodeResourceIds]);

  return {
    connections,
    resources,
    hooks,
    actions,
    models,
    validations,
    hash: JSON.stringify(agent),
    isPending: isPendingAgent || isPendingHooks || isPendingActions || isPendingModels || isPendingValidations,
  };
};
